"use client";

import { useReducedMotion, motion } from "framer-motion";
import { Building2, House as Home, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, MapPin } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function BracknellIntro() {
  const shouldReduceMotion = useReducedMotion();
  const highlights = [{
    title: "Commercial town-centre projects",
    desc: "High-capacity independent scaffolding, edge protection, and loading towers for Bracknell retail and office redevelopments.",
    icon: Building2
  }, {
    title: "New-build residential estates",
    desc: "Phased progressive lifts, internal birdcages, and roof access tailored for active housing developments across Berkshire.",
    icon: Home
  }, {
    title: "Established suburban housing",
    desc: "Careful domestic access solutions for chimney repointing, roof replacements, solar installations, and home extensions.",
    icon: CheckCircle2
  }];
  const credentials = [{
    label: "NASC accredited member",
    detail: "Audited safety & compliance"
  }, {
    label: "CHAS accredited contractor",
    detail: "Rigorous health & safety standards"
  }, {
    label: "20+ years direct leadership",
    detail: "Led by industry director Pete Wood"
  }];
  return <section id="bracknellintro" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Main Grid: Content and Quick Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          
          {/* Main Copy (answer-first intro) */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-border text-foreground px-3 py-1 font-medium text-xs flex items-center gap-1.5">
                <MapPin className="size-3.5 text-primary" />
                <span>Bracknell &amp; Berkshire coverage</span>
              </Badge>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
              Commercial &amp; residential scaffolding in Bracknell
            </h2> 

            <div className="space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              <p className="font-medium text-foreground">
                BMF Scaffolding provides commercial, residential, roofing, solar and specialist scaffolding in Bracknell and surrounding Berkshire areas, including RG12.
              </p>
              <p>
                From modern town-centre commercial redevelopments and rapid-build residential estates to established suburban housing, our teams erect safe, compliant, and robust structures designed for your specific site requirements.
              </p>
              <p>
                With full NASC and CHAS accreditation and over 20 years of hands-on experience under director Pete Wood, we deliver dependable access with strict safety compliance, clear scheduling, and professional on-site supervision for every project.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactRequestQuotePage">
                  Request a Bracknell quote
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-accent">
                <Link to="/AreasWeCoverPage">
                  View all coverage areas
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Key Local Context Cards */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Card className="bg-card text-card-foreground border border-border shadow-sm">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2 text-foreground font-semibold text-base font-default">
                  <ShieldCheck className="size-5 text-primary" />
                  <span>Accreditations &amp; experience</span>
                </div>

                <div className="divide-y divide-border text-sm">
                  {credentials.map((cred, i) => <div key={cred.label} data-index={i} className="py-2.5 first:pt-0 last:pb-0 flex flex-col">
                      <span className="font-medium text-foreground">{cred.label}</span>
                      <span className="text-muted-foreground text-xs">{cred.detail}</span>
                    </div>)}
                </div>
              </CardContent>
            </Card>

            {/* Local application cards */}
            <div className="grid grid-cols-1 gap-3">
              {highlights.map((item, i) => {
              const IconComponent = item.icon;
              return <motion.div key={item.title} data-index={i} initial={shouldReduceMotion ? {
                opacity: 1
              } : {
                opacity: 0,
                y: 12
              }} whileInView={shouldReduceMotion ? {
                opacity: 1
              } : {
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: i * 0.1
              }}>
                    <div className="p-4 rounded-lg bg-muted/50 border border-border/70 flex gap-3.5 items-start">
                      <div className="p-2 rounded-md bg-card text-primary shrink-0 border border-border">
                        <IconComponent className="size-4" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground font-default">
                          {item.title}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  </motion.div>;
            })}
            </div>

          </div>

        </div>
      </div>
    </section>;
}